import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Link2, Download, Plus, Search, Trash2, Users, CheckCircle2,
  XCircle, Clock, UtensilsCrossed, Settings2, X, Eye, ChevronDown, FileText,
  Salad, AlertTriangle, Sparkles, ClipboardList, SlidersHorizontal, Share2,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi, fileUrl, downloadGuestExport } from "@/lib/api";
import { eventPath, eventUrl, eventShareUrl } from "@/lib/eventUrl";
import { CustomizeExportDialog } from "@/components/export/CustomizeExportDialog";
import { formatShortDate } from "@/lib/helpers";
import { useCan } from "@/context/AuthContext";

const STATUS_STYLES = {
  attending: "bg-emerald-100 text-emerald-800",
  declined: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
};

const DIETARY_LABELS = {
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  gluten_free: "Gluten-free",
  nut_allergy: "Nut allergy",
  other_allergy: "Other allergy / restriction",
  none: "No restriction",
};

const inputCls = "w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function EventManage() {
  const canDelete = useCan("delete");
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [newGuest, setNewGuest] = useState({ name: "", email: "", status: "pending" });

  const load = useCallback(async () => {
    try {
      const [ev, gs, st] = await Promise.all([
        eventsApi.get(id),
        eventsApi.guests(id),
        eventsApi.stats(id),
      ]);
      setEvent(ev);
      setGuests(gs);
      setStats(st);
    } catch {
      toast.error("Failed to load event");
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    navigator.clipboard.writeText(eventUrl(event));
    toast.success("RSVP link copied");
  };
  const copyShareLink = () => {
    navigator.clipboard.writeText(eventShareUrl(event));
    toast.success("WhatsApp / social share link copied");
  };

  const addGuest = async () => {
    if (!newGuest.name.trim()) { toast.error("Name required"); return; }
    await eventsApi.addGuest(id, { ...newGuest, party_size: 1 });
    toast.success("Guest added");
    setNewGuest({ name: "", email: "", status: "pending" });
    setShowAdd(false);
    load();
  };

  const removeGuest = async (gid) => {
    await eventsApi.removeGuest(id, gid);
    toast.success("Guest removed");
    setDetail(null);
    load();
  };

  const exportAll = async () => {
    try {
      await downloadGuestExport(id, { format: "xlsx", keys: null, status: filter, q });
      toast.success("Guest list exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = guests.filter((g) => {
    const matchQ = !q || g.name.toLowerCase().includes(q.toLowerCase()) || (g.email || "").toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || g.status === filter;
    return matchQ && matchF;
  });

  if (!event || !stats) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div>;
  }

  const statCards = [
    { icon: Users, label: "Total invited", value: stats.total_invited, testId: "stat-total-invited", cls: "bg-secondary text-foreground" },
    { icon: CheckCircle2, label: "Attending", value: stats.attending, testId: "stat-total-attending", cls: "bg-emerald-50 text-emerald-700" },
    { icon: XCircle, label: "Declined", value: stats.declined, testId: "stat-total-declined", cls: "bg-red-50 text-red-700" },
    { icon: Clock, label: "Pending", value: stats.pending, testId: "stat-total-pending", cls: "bg-amber-50 text-amber-700" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <CustomizeExportDialog open={showExport} onOpenChange={setShowExport} eventId={id} defaultStatus={filter} />
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>

        {/* header */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
          <div className="relative h-32 bg-cover bg-center sm:h-40" style={{ backgroundImage: `url(${event.cover_image})` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-end justify-between gap-3 text-white">
              <div>
                <p className="eyebrow text-gold">{event.event_type} • {formatShortDate(event.date)}</p>
                <h1 className="font-serif text-2xl font-semibold sm:text-3xl">{event.title}</h1>
              </div>
              <div className="flex gap-2">
                <Link to={`/events/${id}/forms`} data-testid="manage-forms-btn" className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur transition-colors hover:bg-white/25">
                  <ClipboardList className="h-4 w-4" /> Guest Forms
                </Link>
                <Link to={eventPath(event)} target="_blank" data-testid="view-public-btn" className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur transition-colors hover:bg-white/25">
                  <Eye className="h-4 w-4" /> View page
                </Link>
                <button onClick={copyLink} data-testid="copy-rsvp-link-btn" className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-primary transition-opacity hover:opacity-90">
                  <Link2 className="h-4 w-4" /> Copy link
                </button>
                <button onClick={copyShareLink} data-testid="copy-share-link-btn" title="Link with event title & image preview for WhatsApp, Facebook etc." className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur transition-colors hover:bg-white/25">
                  <Share2 className="h-4 w-4" /> WhatsApp link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((s) => (
            <div key={s.label} data-testid={s.testId} className="rounded-2xl border border-border bg-card p-5">
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.cls}`}><s.icon className="h-5 w-5" /></span>
              <p className="mt-4 font-serif text-3xl font-semibold">{s.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Response rate</span>
            <span className="font-serif text-lg font-semibold text-gold">{stats.response_rate}%</span>
          </div>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-gold" />
            <span className="text-muted-foreground">Guests coming</span>
            <span className="font-semibold">{stats.total_heads}</span>
          </div>
        </div>

        {/* meal breakdown */}
        {Object.keys(stats.meal_breakdown || {}).length > 0 && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-gold" /><h3 className="font-semibold">Meal breakdown</h3></div>
            <div className="mt-4 space-y-3">
              {Object.entries(stats.meal_breakdown).map(([meal, count]) => {
                const max = Math.max(...Object.values(stats.meal_breakdown));
                return (
                  <div key={meal}>
                    <div className="flex justify-between text-sm"><span>{meal}</span><span className="font-semibold">{count}</span></div>
                    <div className="mt-1 h-2 rounded-full bg-secondary"><div className="h-2 rounded-full bg-gold" style={{ width: `${(count / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* AI insights: dietary needs + follow-ups (Jev) */}
        {(Object.keys(stats.dietary_breakdown || {}).length > 0 || stats.followup_count > 0) && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="dietary-summary-card">
              <div className="flex items-center gap-2">
                <Salad className="h-5 w-5 text-gold" />
                <h3 className="font-semibold">Dietary needs</h3>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold"><Sparkles className="h-3 w-3" /> AI tagged</span>
              </div>
              {Object.keys(stats.dietary_breakdown || {}).length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(stats.dietary_breakdown).map(([tag, count]) => (
                    <span key={tag} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-sm">
                      {DIETARY_LABELS[tag] || tag}
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-xs font-bold text-primary">{count}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">No special dietary needs reported yet.</p>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card p-5" data-testid="followup-summary-card">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h3 className="font-semibold">Needs follow-up</h3>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold"><Sparkles className="h-3 w-3" /> AI flagged</span>
              </div>
              {stats.followup_count > 0 ? (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-muted-foreground"><b className="text-foreground">{stats.followup_count}</b> guest{stats.followup_count > 1 ? "s have" : " has"} an actionable request (shuttle, accessibility, late arrival, etc.)</p>
                  <div className="flex flex-wrap gap-2">
                    {guests.filter((g) => g.needs_followup).map((g) => (
                      <button key={g.id} onClick={() => setDetail(g)} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100">
                        <AlertTriangle className="h-3.5 w-3.5" /> {g.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">No follow-ups flagged.</p>
              )}
            </div>
          </div>
        )}

        {/* guest list */}
        <div className="mt-6 rounded-2xl border border-border bg-card">
          <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
            <h2 className="font-serif text-xl font-semibold">Guest list</h2>
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="guest-search-input" className={`${inputCls} pl-9`} placeholder="Search guests..." value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="relative">
              <select data-testid="guest-filter-status-select" className={`${inputCls} appearance-none pr-9`} value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="attending">Attending</option>
                <option value="declined">Declined</option>
                <option value="pending">Pending</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
            <button onClick={exportAll} data-testid="export-csv-btn" title="Export all fields for the guests shown (Excel)" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-secondary">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={() => setShowExport(true)} data-testid="customize-export-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-gold/60 bg-gold/10 px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-gold/20">
              <SlidersHorizontal className="h-4 w-4 text-gold" /> Customize Export
            </button>
            <button onClick={() => setShowAdd(true)} data-testid="add-guest-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
              <Plus className="h-4 w-4" /> Add guest
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="guest-list-table">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-5 py-3 font-medium">Guest</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Party</th>
                  <th className="px-5 py-3 font-medium">Meal</th>
                  <th className="px-5 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">No guests match your filters.</td></tr>
                ) : filtered.map((g) => (
                  <tr key={g.id} data-testid={`guest-row-${g.id}`} className="border-b border-border/60 transition-colors hover:bg-secondary/40">
                    <td className="px-5 py-3">
                      <button onClick={() => setDetail(g)} className="text-left">
                        <p className="flex items-center gap-1.5 font-medium">
                          {g.name}
                          {g.needs_followup && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Needs follow-up" data-testid={`followup-flag-${g.id}`} />}
                        </p>
                        <p className="text-xs text-muted-foreground">{g.email || "—"}</p>
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[g.status]}`}>{g.status}</span>
                    </td>
                    <td className="px-5 py-3">{g.status === "attending" ? g.party_size : "—"}</td>
                    <td className="px-5 py-3 max-w-[220px] truncate text-muted-foreground">
                      {g.meal_choice || "—"}
                      {g.dietary_tag && g.dietary_tag !== "none" && (
                        <span className="ml-2 inline-flex rounded-full bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold">{DIETARY_LABELS[g.dietary_tag] || g.dietary_tag}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setDetail(g)} className="rounded-lg p-2 hover:bg-secondary"><Eye className="h-4 w-4" /></button>
                        {canDelete && (<button onClick={() => removeGuest(g.id)} data-testid={`delete-guest-${g.id}`} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add guest modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-guest-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold">Add a guest</h3>
              <button onClick={() => setShowAdd(false)} className="rounded-lg p-1 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <input data-testid="new-guest-name" className={inputCls} placeholder="Full name" value={newGuest.name} onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })} />
              <input data-testid="new-guest-email" className={inputCls} placeholder="Email (optional)" value={newGuest.email} onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })} />
              <select className={inputCls} value={newGuest.status} onChange={(e) => setNewGuest({ ...newGuest, status: e.target.value })}>
                <option value="pending">Pending (invited)</option>
                <option value="attending">Attending</option>
                <option value="declined">Declined</option>
              </select>
              <button onClick={addGuest} data-testid="save-guest-btn" className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground">Add guest</button>
            </div>
          </div>
        </div>
      )}

      {/* Guest detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="guest-detail-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold">{detail.name}</h3>
              <button onClick={() => setDetail(null)} className="rounded-lg p-1 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>
            {detail.needs_followup && (
              <span className="mt-2 ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800"><AlertTriangle className="h-3.5 w-3.5" /> Needs follow-up</span>
            )}
            {detail.dietary_tag && detail.dietary_tag !== "none" && (
              <span className="mt-2 ml-2 inline-flex items-center gap-1 rounded-full bg-gold/10 px-3 py-1 text-xs font-semibold text-gold"><Salad className="h-3.5 w-3.5" /> {DIETARY_LABELS[detail.dietary_tag] || detail.dietary_tag}</span>
            )}
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd>{detail.email || "—"}</dd></div>
              {detail.status === "attending" && <div className="flex justify-between"><dt className="text-muted-foreground">Party size</dt><dd>{detail.party_size}</dd></div>}
              {detail.meal_choice && <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Meal</dt><dd className="text-right">{detail.meal_choice}</dd></div>}
              {detail.plus_one_names?.length > 0 && <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Guests</dt><dd className="text-right">{detail.plus_one_names.join(", ")}</dd></div>}
              {detail.plus_one_meals?.filter(Boolean).length > 0 && <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Guest meals</dt><dd className="text-right">{detail.plus_one_meals.filter(Boolean).join(", ")}</dd></div>}
              {Object.entries(detail.custom_answers || {}).map(([k, v]) => (
                Array.isArray(v) ? (
                  <div key={k}>
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="mt-1 space-y-1.5">
                      {v.filter(Boolean).map((item, idx) => (
                        <a key={idx} href={fileUrl(item.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm font-medium hover:bg-secondary">
                          <FileText className="h-4 w-4 text-gold" />
                          <span className="flex-1 truncate">{item.name}</span>
                          <span className="text-xs text-gold">View ID</span>
                        </a>
                      ))}
                    </dd>
                  </div>
                ) : (
                  <div key={k} className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt><dd className="text-right">{String(v)}</dd></div>
                )
              ))}
              {detail.message && <div><dt className="text-muted-foreground">Note</dt><dd className="mt-1 rounded-lg bg-secondary/50 p-3 italic">"{detail.message}"</dd></div>}
            </dl>
            {canDelete && (<button onClick={() => removeGuest(detail.id)} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-destructive">
              <Trash2 className="h-4 w-4" /> Remove guest
            </button>)}
          </div>
        </div>
      )}
    </div>
  );
}
